import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { terrainHeight, terrainSlope } from './terrain';
import { biomeAt, forestDensity, Biome } from './biome';
import { TERRAIN } from '../config';
import { applyInteriorDark } from './interior';
import { applyCloudShadow } from './clouds';

// GROUND CLUTTER: the small stuff that makes ground read as ground up close. Six instanced kinds,
// each only where it MAKES SENSE:
//   pebbles   -- everywhere below the snow line, thickest on rocky/desert ground and beaches
//   sticks    -- only near trees (per-item forestDensity gate), never on beach/snow
//   leaves    -- pool under HEAVY canopy (quadratic forestDensity gate), rare at forest fringes,
//                absent on open meadows/beaches/snow
//   shells    -- beach sand only
//   starfish  -- beach sand, rarer than shells
//   snowpiles -- above the snow line only (where the terrain shader paints snow)
//
// Placement is DETERMINISTIC: each 24m cell regenerates the exact same items from a seeded hash every
// time it streams in -- walking away and back never re-rolls the world. Only whole cells enter/leave
// the streamed set (no per-item radial cull), and the vertex shader grows items in from the ground
// over the last metres of the draw radius, so nothing pops. Purely cosmetic: no collision, no shadows.

const CELL = 24;              // generation cell (stream rebuild happens on cell cross)
const DRAW_R = 48;            // cells within this range of the camera are streamed in
const FADE_START = 34;        // shader grow-in band: full size inside this ...
const FADE_END = 46;          // ... shrunk to nothing past this (inside DRAW_R so edges are invisible)
const CLUTTER_SEED = 20260701;

const CAPS = { pebble: 1800, stick: 900, leaf: 2000, shell: 500, star: 260 } as const;
// Per-cell candidate counts at full density (per-kind gates below thin them out).
const PER_CELL = { pebble: 26, stick: 14, leaf: 40, shell: 7, star: 2 } as const;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function cellSeed(cx: number, cz: number): number {
  let h = (Math.imul(cx, 374761393) + Math.imul(cz, 668265263) + CLUTTER_SEED) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

// --- the little geometries (built once) --------------------------------------------------------------

// A pebble: a squashed, lightly-lumped icosahedron. Detail 0 keeps it faceted (reads as chipped stone).
function makePebble(): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, 0);
  const pos = g.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const l = 1 + 0.18 * Math.sin(v.x * 5.1 + v.z * 3.7) * Math.sin(v.y * 4.3);
    v.multiplyScalar(l);
    pos.setXYZ(i, v.x, v.y * 0.62, v.z); // squashed flat-ish
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

// A fallen twig: two thin tapered boxes at a slight kink, so it reads bent, not machined.
function makeStick(): THREE.BufferGeometry {
  const a = new THREE.BoxGeometry(0.030, 0.028, 0.52);
  a.translate(0, 0, -0.24);
  const b = new THREE.BoxGeometry(0.024, 0.022, 0.38);
  b.rotateY(0.5);
  b.translate(0.085, 0, 0.16);
  const g = mergeGeometries([a, b], false)!;
  a.dispose(); b.dispose();
  g.computeVertexNormals();
  return g;
}

// A fallen leaf: one small bent quad (two triangles), double-sided material.
function makeLeaf(): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(0.14, 0.2, 1, 2);
  g.rotateX(-Math.PI / 2);
  const pos = g.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) { // slight lengthwise curl so it isn't a flat sticker
    v.fromBufferAttribute(pos, i);
    pos.setY(i, 0.02 * Math.cos((v.z / 0.1) * 1.4));
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

// A beach shell: a low ribbed half-dome (the wavy rim reads as a scallop at this size).
function makeShell(): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(1, 10, 4, 0, Math.PI * 2, 0, Math.PI / 2);
  const pos = g.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const a = Math.atan2(v.z, v.x);
    const rib = 1 + 0.10 * Math.sin(a * 9.0); // radial ribs
    pos.setXYZ(i, v.x * rib, v.y * 0.42, v.z * rib);
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

// A starfish: a five-arm star outline, extruded thin with a bevel.
function makeStar(): THREE.BufferGeometry {
  const s = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? 1 : 0.42;
    if (i === 0) s.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else s.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  s.closePath();
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.16, bevelEnabled: true, bevelThickness: 0.08, bevelSize: 0.1, bevelSegments: 1, curveSegments: 4 });
  g.rotateX(-Math.PI / 2); // lie flat
  g.computeVertexNormals();
  return g;
}

// Litter palettes (linear RGB), tinted per instance for variety.
function lin(h: number): THREE.Color { return new THREE.Color(h).convertSRGBToLinear(); }
const PEBBLE_COLS = [0x8a8f96, 0x7c7f78, 0x9a948a, 0x6f7278, 0xa39d8d].map(lin);
const STICK_COLS = [0x6b4a2b, 0x5a3f26, 0x7a5a35, 0x4f3a22].map(lin);
const DRY_STICK_COLS = [0xb5a888, 0xa89878, 0xc2b294].map(lin); // sun-bleached desert wood
const LEAF_COLS = [0xa8783a, 0x8f6a30, 0xb08a45, 0x7d6a33, 0x9a5f2c, 0x6f7d3a].map(lin); // autumn litter + a green straggler
const SHELL_COLS = [0xe8dcc8, 0xd9c4ad, 0xe3cfd2, 0xcfc3ae].map(lin);
const STAR_COLS = [0xd97a4a, 0xc4643c, 0xb98a5a].map(lin);

// Vertex-shader GROW-IN: items scale up from nothing over the last metres of the draw radius (per
// frame, camera-relative), so streaming never pops. Chained before the cloud-shadow injection.
function applyGrowIn(mat: THREE.Material, key: string): void {
  const prevCompile = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey;
  mat.onBeforeCompile = (shader, renderer) => {
    prevCompile.call(mat, shader, renderer);
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      {
        vec4 oxCC = modelMatrix * (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0));
        float oxCF = 1.0 - smoothstep(${FADE_START.toFixed(1)}, ${FADE_END.toFixed(1)}, distance(oxCC.xz, cameraPosition.xz));
        transformed *= oxCF;
      }`,
    );
  };
  mat.customProgramCacheKey = () => prevKey.call(mat) + '|growin1-' + key;
}

interface Kind { mesh: THREE.InstancedMesh; cap: number; n: number; }

export class GroundClutter {
  readonly group = new THREE.Group();
  private readonly kinds: Record<keyof typeof CAPS, Kind>;
  private streamCellX = NaN; private streamCellZ = NaN;
  private readonly m = new THREE.Matrix4();
  private readonly p = new THREE.Vector3();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler();
  private readonly s = new THREE.Vector3();

  constructor() {
    const mk = (geo: THREE.BufferGeometry, key: keyof typeof CAPS, rough: number, doubleSide = false): Kind => {
      const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: rough, metalness: 0, flatShading: true, side: doubleSide ? THREE.DoubleSide : THREE.FrontSide });
      applyGrowIn(mat, key);          // camera-distance grow-in (no pop at the stream edge)
      applyInteriorDark(mat, true);   // darkens inside a base like the ground it lies on
      applyCloudShadow(mat, 'clutter-' + key); // drifting cloud shadows match the terrain
      const cap = CAPS[key];
      const mesh = new THREE.InstancedMesh(geo, mat, cap);
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.castShadow = false;
      mesh.receiveShadow = false; // tiny + ground-hugging: cloud shadow + interior dark carry the look
      mesh.frustumCulled = false; // hugs the camera disc anyway
      mesh.count = 0;
      this.group.add(mesh);
      return { mesh, cap, n: 0 };
    };
    this.kinds = {
      pebble: mk(makePebble(), 'pebble', 0.95),
      stick: mk(makeStick(), 'stick', 0.9),
      leaf: mk(makeLeaf(), 'leaf', 0.85, true),
      shell: mk(makeShell(), 'shell', 0.55),
      star: mk(makeStar(), 'star', 0.8),
    };
  }

  // Admin re-gen (terrain knobs changed): force a re-stream so litter re-seats on the new ground.
  regenerate(): void {
    this.streamCellX = NaN; this.streamCellZ = NaN;
  }

  update(camX: number, camZ: number): void {
    const cellX = Math.floor(camX / CELL), cellZ = Math.floor(camZ / CELL);
    if (cellX === this.streamCellX && cellZ === this.streamCellZ) return;
    this.streamCellX = cellX; this.streamCellZ = cellZ;
    this.stream(camX, camZ);
  }

  private put(k: Kind, x: number, y: number, z: number, yaw: number, sx: number, sy: number, sz: number, col: THREE.Color, roll = 0): void {
    if (k.n >= k.cap) return;
    this.p.set(x, y, z);
    this.q.setFromEuler(this.e.set(0, yaw, roll));
    this.s.set(sx, sy, sz);
    this.m.compose(this.p, this.q, this.s);
    k.mesh.setMatrixAt(k.n, this.m);
    k.mesh.setColorAt(k.n, col);
    k.n++;
  }

  // Regenerate the instance buffers from every cell within DRAW_R. Deterministic per cell: the same
  // cell always produces the same items (seeded hash), so nothing ever re-rolls. Whole cells only --
  // no per-item radial cull -- and the shader's grow-in hides the cell edges entirely.
  private stream(camX: number, camZ: number): void {
    for (const k of Object.values(this.kinds)) k.n = 0;
    const K = this.kinds;
    const cx0 = Math.floor((camX - DRAW_R) / CELL), cx1 = Math.floor((camX + DRAW_R) / CELL);
    const cz0 = Math.floor((camZ - DRAW_R) / CELL), cz1 = Math.floor((camZ + DRAW_R) / CELL);
    const snowH = TERRAIN.snowStart;
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const rng = mulberry32(cellSeed(cx, cz));
        const x0 = cx * CELL, z0 = cz * CELL;
        // One biome probe at the cell centre steers the mix; per-item gates refine inside the cell.
        const mx = x0 + CELL / 2, mz = z0 + CELL / 2;
        const mh = terrainHeight(mx, mz);
        if (mh <= TERRAIN.seaLevel - 2.5) continue; // fully underwater cell
        const bm = biomeAt(mx, mz, mh);
        if (bm === Biome.Ocean) continue;
        const beach = bm === Biome.Beach;
        const desert = bm === Biome.Desert;
        const snowBiome = bm === Biome.Snow || mh > snowH;
        const rocky = bm === Biome.Rocky || bm === Biome.Mountain || desert;
        const grassy = bm === Biome.Grassland || bm === Biome.Meadow || bm === Biome.Hills;
        if (beach) {
          // SAND is subtle: shells + starfish hugging the shore band -- NO pebbles, NO sticks, NO leaves.
          for (let k = 0; k < PER_CELL.shell; k++) {
            const x = x0 + rng() * CELL, z = z0 + rng() * CELL, yaw = rng() * Math.PI * 2, sc = 0.035 + rng() * 0.045, ci = (rng() * SHELL_COLS.length) | 0;
            const h = terrainHeight(x, z);
            if (h <= TERRAIN.seaLevel + 0.05 || h > TERRAIN.seaLevel + 6) continue;
            this.put(K.shell, x, h + 0.008, z, yaw, sc, sc, sc, SHELL_COLS[ci]);
          }
          for (let k = 0; k < PER_CELL.star; k++) {
            const x = x0 + rng() * CELL, z = z0 + rng() * CELL, yaw = rng() * Math.PI * 2, sc = 0.05 + rng() * 0.05, ci = (rng() * STAR_COLS.length) | 0;
            const h = terrainHeight(x, z);
            if (h <= TERRAIN.seaLevel + 0.05 || h > TERRAIN.seaLevel + 4.5) continue;
            this.put(K.star, x, h + 0.01, z, yaw, sc, sc * 0.5, sc, STAR_COLS[ci]);
          }
          continue;
        }
        if (snowBiome) continue; // snow stays CLEAN (the owner binned the snow piles; trails carry the interest)
        // PEBBLES: a rocky/desert thing -- sparse elsewhere, so open grass doesn't read as gravel.
        const pebbleN = Math.round(PER_CELL.pebble * (rocky ? 1.1 : grassy ? 0.22 : 0.4));
        for (let k = 0; k < pebbleN; k++) {
          const x = x0 + rng() * CELL, z = z0 + rng() * CELL, yaw = rng() * Math.PI * 2, sc = 0.05 + rng() * 0.10, ci = (rng() * PEBBLE_COLS.length) | 0, ws = 0.8 + rng() * 0.5;
          const h = terrainHeight(x, z);
          if (h <= TERRAIN.seaLevel - 0.3 || h > snowH || terrainSlope(x, z, 1.0) > 0.85) continue;
          this.put(K.pebble, x, h + sc * 0.30, z, yaw, sc * ws, sc, sc, PEBBLE_COLS[ci]);
        }
        // STICKS: near trees only (per-item forest gate) -- EXCEPT the desert, which gets its own sparse
        // sun-bleached dry wood instead of forest litter.
        const stickN = desert ? 4 : PER_CELL.stick;
        for (let k = 0; k < stickN; k++) {
          const x = x0 + rng() * CELL, z = z0 + rng() * CELL, yaw = rng() * Math.PI * 2, sc = 0.6 + rng() * 0.9, keep = rng();
          const pal = desert ? DRY_STICK_COLS : STICK_COLS;
          const ci = (rng() * pal.length) | 0;
          if (!desert) {
            const fd = forestDensity(x, z);
            if (fd < 0.3 || keep > (fd - 0.3) * 1.7) continue; // no trees around = no debris
          } else if (keep > 0.5) continue; // desert dry wood stays sparse
          const h = terrainHeight(x, z);
          if (h <= TERRAIN.seaLevel + 0.2 || h > snowH || terrainSlope(x, z, 1.0) > 0.7) continue;
          this.put(K.stick, x, h + 0.015 * sc, z, yaw, sc, sc, sc, pal[ci], (rng() - 0.5) * 0.08);
        }
        if (desert) continue; // desert gets no leaf litter
        // LEAF LITTER: pools under HEAVY canopy (quadratic gate); absent on open ground.
        for (let k = 0; k < PER_CELL.leaf; k++) {
          const x = x0 + rng() * CELL, z = z0 + rng() * CELL, yaw = rng() * Math.PI * 2, sc = 0.7 + rng() * 0.8, ci = (rng() * LEAF_COLS.length) | 0, keep = rng();
          const fd = forestDensity(x, z);
          const density = fd < 0.45 ? 0 : (fd - 0.45) * (fd - 0.45) * 9.0; // 0 at fringe, ~1 in deep forest
          if (keep > density) continue;
          const h = terrainHeight(x, z);
          if (h <= TERRAIN.seaLevel + 0.2 || h > snowH || terrainSlope(x, z, 1.0) > 0.7) continue;
          this.put(K.leaf, x, h + 0.012, z, yaw, sc, sc, sc, LEAF_COLS[ci]);
        }
      }
    }
    for (const k of Object.values(this.kinds)) {
      k.mesh.count = k.n;
      k.mesh.instanceMatrix.needsUpdate = true;
      if (k.mesh.instanceColor) k.mesh.instanceColor.needsUpdate = true;
    }
  }
}
